import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  BarCode,
  ArrowRight2,
  FileUpload,
} from "../CustomComponents/SwiftIcon/Icons";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import Pulse from "../Loader/Pulse";
import {
  checkLength,
  isEmpty,
  isValidEmail,
  isValidPan,
  isValidPhoneNumber,
} from "../../exports/UtilFunctions";
import ServerRequest from "../../exports/ServerRequest";
import AccessHeader from "./AccessHeader";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

// Named steps: every comparison goes through these, so the order lives here
// and nowhere else.
const STEP = { PAN: 1, EMAIL: 2, MOBILE: 3, AADHAAR: 4, DONE: 5 };

const AADHAAR_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

// DigiLocker replaces this tab with its own page, so what the user has already
// entered has to be parked somewhere that survives the round trip. Cleared the
// moment it is read back.
const DL_STASH = "sf_signup_digilocker";

function AccessSignupNew() {
  const [step, setStep] = useState(1);

  const [email, setEmail] = useState("");
  const [otp, setOTP] = useState("");
  const [number, setNumber] = useState("");
  const [pan, setPan] = useState("");

  const [timer, setTimer] = useState(0);

  const [Error1, setError1] = useState("");
  const [Error2, setError2] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);

  const [otpSent, setOTPSent] = useState(false);

  const [noPan, setNoPan] = useState(false);
  const [incorrectPan, setIncorrectPan] = useState(false);

  const [panDetails, setPanDetails] = useState(null);

  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [pdfPassword, setPdfPassword] = useState("");
  const [needsPdfPassword, setNeedsPdfPassword] = useState(false);
  const [aadhaarAddress, setAadhaarAddress] = useState(null);

  // null while we are still asking, true/false once we know whether the mobile
  // verified in step 3 has a DigiLocker account behind it.
  const [dlAvailable, setDlAvailable] = useState(null);
  const [aadhaarMethod, setAadhaarMethod] = useState(null);

  const handleNext = () => {
    setStep(step + 1);
  };

  const DEVICE_SIZE = getDeviceSize();
  const BUTTON_SIZE = DEVICE_SIZE === "S" ? "btn-xxl" : "btn-xl";

  // Shared by the picker and by anything else that hands us a file. `accept`
  // only filters the file dialog, so the type is checked here too.
  function pickAadhaarFile(file) {
    if (!file) return;

    if (!AADHAAR_ACCEPT.split(",").includes(file.type)) {
      setError2("Please upload a JPG, PNG or PDF file");
      return;
    }

    setAadhaarFile(file);
    setError1("");
    setError2("");
    // A new file invalidates whatever password the last one needed.
    setNeedsPdfPassword(false);
    setPdfPassword("");
  }

  function resetAadhaar() {
    setAadhaarAddress(null);
    setAadhaarFile(null);
    setAadhaarMethod(null);
    setNeedsPdfPassword(false);
    setPdfPassword("");
    setError1("");
    setError2("");
  }

  // Coming back from DigiLocker. The id in the URL is only a claim that the
  // journey finished -- the address itself is read from Cashfree server-side,
  // never from anything the redirect carries.
  useEffect(() => {
    // Ours is `dl`, on the redirect_url we handed Cashfree. Cashfree appends
    // `verification_id` of its own on top, so either one gets us home.
    const params = new URLSearchParams(window.location.search);
    const verification_id = params.get("dl") || params.get("verification_id");
    if (!verification_id) return;

    const saved = sessionStorage.getItem(DL_STASH);
    sessionStorage.removeItem(DL_STASH);
    window.history.replaceState({}, "", window.location.pathname);

    let state = null;
    try {
      state = JSON.parse(saved);
    } catch (e) {
      state = null;
    }

    // A different browser, a cleared tab, a bookmarked callback: nothing to
    // resume, so start over rather than half-restore.
    if (!state?.pan || !state?.panDetails) {
      Alert({
        TitleText: "Warning",
        Message: "Your sign-up session expired. Please start again.",
        ...warning_config,
      });
      return;
    }

    setPan(state.pan);
    setEmail(state.email || "");
    setNumber(state.number || "");
    setPanDetails(state.panDetails);
    setDlAvailable(true);
    setAadhaarMethod("digilocker");
    setStep(STEP.AADHAAR);

    (async () => {
      setLoading(true);
      setLoadingMessage("Fetching your Aadhaar from DigiLocker...");

      const data = await ServerRequest({
        method: "POST",
        url: "/access/signup/digilocker/fetch",
        data: { verification_id, pan: state.pan },
      });

      setLoading(false);
      setLoadingMessage("");

      if (data?.status === "SUCCESS") {
        setAadhaarAddress(data.address);
        return;
      }

      // Back to the chooser with the reason showing: most DigiLocker failures
      // are answerable by retrying or by uploading instead.
      setAadhaarMethod(null);
      setError2(
        data?.message || "DigiLocker verification could not be completed."
      );
    })();
  }, []);

  // Whether to offer DigiLocker at all. Asked once per visit to the step.
  useEffect(() => {
    if (step !== STEP.AADHAAR || dlAvailable !== null || !number) return;

    let cancelled = false;

    (async () => {
      const data = await ServerRequest({
        method: "POST",
        url: "/access/signup/digilocker/check",
        data: { mobile: number },
      });

      // The server answers "no" for its own failures too, so a bad day at
      // Cashfree drops the user onto the upload path instead of blocking them.
      if (!cancelled) setDlAvailable(Boolean(data?.exists));
    })();

    return () => {
      cancelled = true;
    };
  }, [step, number, dlAvailable]);

  async function startDigiLocker() {
    setError1("");
    setError2("");
    setLoading(true);
    setLoadingMessage("Opening DigiLocker...");

    const data = await ServerRequest({
      method: "POST",
      url: "/access/signup/digilocker/init",
      data: {},
    });

    if (data?.status !== "SUCCESS" || !data?.url) {
      setLoading(false);
      setLoadingMessage("");
      setAadhaarMethod("upload");
      setError2(
        data?.message ||
          "DigiLocker is unavailable. Please upload your Aadhaar document."
      );
      return;
    }

    sessionStorage.setItem(
      DL_STASH,
      JSON.stringify({ pan, email, number, panDetails })
    );
    window.location.assign(data.url);
  }

  async function submitAadhaar() {
    if (!aadhaarFile) {
      setError2("Please upload your Aadhaar document");
      return;
    }

    const form = new FormData();
    form.append("pan", pan);
    form.append("document", aadhaarFile);
    if (pdfPassword) form.append("pdf_password", pdfPassword);

    try {
      setLoading(true);
      setLoadingMessage("Reading your Aadhaar document...");
      setError1("");
      setError2("");

      const data = await ServerRequest({
        method: "POST",
        url: "/access/signup/aadhaar",
        data: form,
      });

      if (data?.status === "SUCCESS") {
        // Hold on this step and show what was read, so the address can be
        // checked before it becomes the KYC address of record.
        setAadhaarAddress(data.address);
        setNeedsPdfPassword(false);
        return;
      }

      // The password cases are the only ones the user can answer inline;
      // everything else means uploading a different document. Error1 is the
      // password field's slot, so the message lands under the field it is
      // actually about.
      if (
        data?.code === "AADHAAR_PDF_PASSWORD_REQUIRED" ||
        data?.code === "AADHAAR_PDF_INVALID_PASSWORD"
      ) {
        setNeedsPdfPassword(true);
        if (data.code === "AADHAAR_PDF_INVALID_PASSWORD") setPdfPassword("");
        setError1(data.message);
        return;
      }

      setError2(data?.message || "Aadhaar verification failed.");
    } catch (err) {
      Alert({
        TitleText: "Warning",
        Message: "Unable to verify the Aadhaar document. Please try again.",
        ...warning_config,
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  }

  useEffect(() => {
    const counter = timer > 0 && setInterval(() => setTimer(timer - 1), 1000);
    return () => clearInterval(counter);
  }, [timer]);

  async function sendOTP() {
    if (timer > 0) {
      return;
    }

    // Neither branch below matches on any other step, and a fall-through here
    // reads as success -- which used to set otpSent on the PAN step.
    if (step !== STEP.EMAIL && step !== STEP.MOBILE) return;

    let isValid = false;
    let errorMessage = "";

    let data = false;

    if (step === 2) {
      if (isEmpty(email) || !isValidEmail(email)) {
        errorMessage = "Please enter a valid email address";
        isValid = false;
      } else {
        setLoading(true);
        setLoadingMessage("Sending one-time password...");

        data = await ServerRequest({
          method: "POST",
          url: `/access/signup/sendotp`,
          data: {
            type: "email",
            value: email,
          },
        });
      }
    } else if (step === 3) {
      if (isEmpty(number) || !isValidPhoneNumber(number)) {
        errorMessage = "Please enter a valid phone number";
        isValid = false;
      } else {
        setLoading(true);
        setLoadingMessage("Sending one-time password...");
        data = await ServerRequest({
          method: "POST",
          url: `/access/signup/sendotp`,
          data: {
            type: "mobile",
            value: number,
          },
        });
      }
    }

    if (errorMessage || data?.error) {
      errorMessage =
        errorMessage ||
        data?.message ||
        "Failed to send OTP. Please try again.";
      isValid = false;
    } else {
      isValid = true;
    }

    if (!isValid) {
      Alert({
        TitleText: "Warning",
        Message: errorMessage,
        ...warning_config,
      });
      setLoading(false);
      setLoadingMessage("");
      setError1("");
      setError2("");
    } else {
      setTimeout(() => {
        setError1("");
        setError2("");
        setLoading(false);
        setLoadingMessage("");

        // Only the email and mobile steps reach here, and both are waiting
        // for an OTP.
        setOTPSent(true);
        setTimer(30);
      }, 2000);
    }
  }

  async function validateOtp() {
    if (isEmpty(otp) || !checkLength(otp, 6)) {
      setError1("");
      setError2("Please enter correct otp");
    } else {
      setLoading(true);
      setLoadingMessage("Validating one time password...");

      const data = await ServerRequest({
        url: `/access/signup/validateotp`,
        data: {
          type: step === 2 ? "email" : "number",
          value: step === 2 ? email : number,
          otp: otp,
        },
        method: "post",
      });

      if (!data.error) {
        setTimeout(() => {
          setTimer(0);
          setError1("");
          setError2("");
          setLoading(false);
          setLoadingMessage(false);
          setOTPSent(false);
          setOTP("");
          handleNext();
        }, 2000);
      } else {
        setTimeout(() => {
          setError1("");
          setError2(data.message);
          setLoading(false);
          setLoadingMessage("");
        }, 1000);
      }
    }
  }

  function KeyUp1(e) {
    if (e.key !== "Enter") return;

    // Shared with the PAN field, where Enter means "fetch", not "send OTP".
    if (step === STEP.PAN) {
      GetPanDetails();
      return;
    }

    sendOTP();
  }

  function KeyUp2(e) {
    if (e.key === "Enter") {
      validateOtp();
    }
  }

  async function handleSubmit() {
    if (!panDetails) return;

    const pan = panDetails?.pan_number;
    const name = panDetails?.user_full_name || "";

    const myForm = new FormData();
    myForm.append("pan", pan);
    myForm.append("name", name);
    myForm.append("email", email);
    myForm.append("mobile", number);

    const user_address = panDetails?.user_address || {};

    // The Aadhaar is the KYC address of record; PAN's copy is the fallback for
    // anyone who got past that step without one.
    const address = aadhaarAddress || {
      address_line_1: user_address?.line_1,
      address_line_2: user_address?.line_2,
      address_line_3: user_address?.street_name,
      city: user_address?.city,
      state: user_address?.state,
      country: user_address?.country,
      pincode: user_address?.zip,
      full: user_address?.full,
    };

    const payload = {
      pan: pan,
      name: name,
      email: email,
      mobile: number,
      address: address,
    };

    // console.log(myForm)
    setLoading(true);
    setLoadingMessage("Creating account, please wait...");

    const data = await ServerRequest({
      method: "POST",
      url: "/access/signup/account",
      data: payload,
      headers: {
        "Content-type": "application/json",
      },
    });

    if (data.error) {
      Alert({
        TitleText: "Warning",
        Message: data?.message,
        ...warning_config,
      });
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    setLoading(false);
    setLoadingMessage("");
    handleNext();
  }

  async function GetPanDetails() {
    if (isEmpty(pan) || !isValidPan(pan)) {
      setError1("");
      setError2("Please enter valid PAN number");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Fetching PAN details...");
      setError1("");
      setError2("");
      const data = await ServerRequest({
        method: "POST",
        url: `/access/signup/validatepan`,
        data: {
          type: "pan",
          value: pan,
        },
      });
      if (data?.error) {
        throw new Error(data?.message || "Failed to fetch PAN details");
      } else {
        const panData = await ServerRequest({
          method: "GET",
          url: `/access/signup/getpan?pan=${pan}`,
        });
        if (!panData?.success) {
          throw new Error(panData?.message || "Failed to fetch PAN details");
        } else if (!panData?.result) {
          throw new Error(
            "Failed to fetch PAN details, Please enter a valid PAN",
          );
        } else {
          setPanDetails(panData?.result);
        }
      }
    } catch (error) {
      Alert({
        TitleText: "Warning",
        Message: error.message,
        ...warning_config,
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  }

  if (step === STEP.DONE) {
    return <Navigate to="/access/success" />;
  }

  return (
    <div className="user-login">
      <div className="user-login-popup-wrapper">
        {loading ? (
          <>
            <div className="popup-loader">
              <Pulse />
              {/* <p>{loadingMessage}</p> */}
            </div>
          </>
        ) : (
          <>
            <div className="login-header">
              <AccessHeader header_text="SignUp" />
            </div>

            {step === 1 && (
              <>
                <div className="login-content">
                  {noPan ? (
                    <>
                      <div className="login-body">
                        <div className="login-notes">
                          <p className="login-note">
                            We only support digital onboarding for valid Aadhaar
                            linked PANs. Please reach out to&nbsp;
                            <strong>hello@swiftfolios.com</strong> or WhatsApp
                            on <strong>+91 8160080658</strong>&nbsp;and we will
                            be happy to assist you in opening an account.
                          </p>
                        </div>
                        <p
                          onClick={() => setNoPan(false)}
                          className="swift-pan-text"
                        >
                          Go Back
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      {panDetails ? (
                        <>
                          {incorrectPan ? (
                            <>
                              <div className="login-body">
                                <div className="login-notes">
                                  <p className="login-note">
                                    We kindly request you to go to{" "}
                                    <a
                                      className="login-link"
                                      target="_blank"
                                      href="https://www.onlineservices.nsdl.com/paam/endUserRegisterContact.html"
                                    >
                                      NSDL Online PAN Card Services - Apply For
                                      New PAN Card & Corrections
                                    </a>{" "}
                                    and correct your address according to your
                                    Aadhaar. It is an online process and will
                                    take only 5 minutes.
                                  </p>
                                  <p className="login-note">
                                    Post updation of your address on your PAN,
                                    you may resume your sign-up process. This is
                                    important as your PAN Data and Aadhaar data
                                    need to be consistent as per Government
                                    Regulations.
                                  </p>
                                  <p className="login-note">
                                    For any query or if you want us to process
                                    this change, please reach out to &nbsp;
                                    <strong>hello@swiftfolios.com</strong> or
                                    WhatsApp on <strong>+91 8160080658</strong>{" "}
                                    and we will be happy to assist you. Charges
                                    of Rs. 72 from NSDL will apply
                                  </p>
                                </div>
                                <p
                                  onClick={() => setIncorrectPan(false)}
                                  className="user-incorrect-details"
                                >
                                  Go Back
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="login-body">
                                <div className="user-data">
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">
                                      Full Name
                                    </p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_full_name || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">
                                      Masked Aadhaar
                                    </p>
                                    <p className="swift-data-value">
                                      {panDetails?.masked_aadhaar || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">Address</p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_address?.full || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">Gender</p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_gender || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">DOB</p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_dob || "-"}
                                    </p>
                                  </div>
                                </div>
                                <p
                                  className="user-incorrect-details"
                                  onClick={() => setIncorrectPan(true)}
                                >
                                  Incorrect details?
                                </p>
                              </div>
                              <div className="login-footer">
                                <div
                                  className={`swift-button-icon ${DEVICE_SIZE === "S" ? "btn-xxl" : "btn-xl"}`}
                                  onClick={handleNext}
                                >
                                  <p>Proceed</p>
                                  {/* <div className="button-icon">
                                    <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                                  </div> */}
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="login-body">
                            <div className="swift-input-box">
                              <p>PAN Number</p>
                              <input
                                className="swift-input"
                                type="text"
                                maxLength={10}
                                value={pan}
                                onChange={(e) => {
                                  setPan(e.target.value.toUpperCase());
                                }}
                                onKeyUp={KeyUp1}
                                placeholder=""
                              />
                              <p
                                className={
                                  Error2 === ""
                                    ? "error-text hide"
                                    : "error-text"
                                }
                              >
                                {Error2}
                              </p>
                            </div>
                            <p
                              onClick={() => setNoPan(true)}
                              className="swift-pan-text"
                            >
                              I don't have a PAN?
                            </p>
                          </div>
                          <div className="login-footer">
                            <div
                              className={`swift-button-icon ${DEVICE_SIZE === "S" ? "btn-xxl" : "btn-xl"}`}
                              onClick={GetPanDetails}
                            >
                              <p>Next</p>
                              {/* <div className="button-icon">
                                <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                              </div> */}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Email</p>
                      <input
                        className="swift-input"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                        }}
                        onKeyUp={KeyUp1}
                        placeholder="Your email address"
                      />
                      <p
                        className={
                          Error1 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {Error1}
                      </p>
                    </div>
                    <div className="swift-button-icon" onClick={sendOTP}>
                      {/* <div className="button-icon">
                        <BarCode size={20} color={COLOR_VARS["SWIFT_COLOR8"]} />
                      </div> */}
                      <p>Generate OTP</p>
                    </div>
                    {otpSent && (
                      <>
                        <p
                          className={
                            timer === 0
                              ? "resend-message hide"
                              : "resend-message"
                          }
                        >
                          Please wait {timer} seconds to resend
                        </p>
                        <div className="swift-input-box">
                          <p>OTP</p>
                          <input
                            className="swift-input"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={otp}
                            placeholder="123456"
                            onKeyUp={KeyUp2}
                            maxLength={6}
                            onChange={(e) => {
                              setOTP(e.target.value.replace(/[^\d]/g, ""));
                            }}
                          />
                          <p
                            className={
                              Error2 === "" ? "error-text hide" : "error-text"
                            }
                          >
                            {Error2}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {otpSent && (
                    <>
                      <div className="login-footer">
                        <div
                          className={`swift-button-icon ${DEVICE_SIZE === "S" ? "btn-xxl" : "btn-xl"}`}
                          onClick={validateOtp}
                        >
                          <p>Next</p>
                          {/* <div className="button-icon">
                            <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                          </div> */}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Mobile Number</p>
                      <input
                        className="swift-input"
                        maxLength={10}
                        type="text"
                        value={number}
                        onChange={(e) => {
                          setNumber(e.target.value.replace(/[^\d]/g, ""));
                        }}
                        onKeyUp={KeyUp1}
                        placeholder="Your mobile number"
                      />
                      <p
                        className={
                          Error1 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {Error1}
                      </p>
                    </div>
                    <div className="swift-button-icon" onClick={sendOTP}>
                      {/* <div className="button-icon">
                        <BarCode size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />
                      </div> */}
                      <p>Generate OTP</p>
                    </div>
                    {otpSent && (
                      <>
                        <p
                          className={
                            timer === 0
                              ? "resend-message hide"
                              : "resend-message"
                          }
                        >
                          Please wait {timer} seconds to resend
                        </p>
                        <div className="swift-input-box">
                          <p>OTP</p>
                          <input
                            className="swift-input"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={otp}
                            placeholder="123456"
                            onKeyUp={KeyUp2}
                            maxLength={6}
                            onChange={(e) => {
                              setOTP(e.target.value.replace(/[^\d]/g, ""));
                            }}
                          />
                          <p
                            className={
                              Error2 === "" ? "error-text hide" : "error-text"
                            }
                          >
                            {Error2}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {otpSent && (
                    <>
                      <div className="login-footer">
                        <div
                          className={`swift-button-icon ${DEVICE_SIZE === "S" ? "btn-xxl" : "btn-xl"}`}
                          onClick={validateOtp}
                        >
                          <p>Next</p>
                          {/* <div className="button-icon">
                            <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                          </div> */}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {step === STEP.AADHAAR && (
              <>
                <div className="login-content">
                  {aadhaarAddress ? (
                    <>
                      <div className="login-body">
                        <div className="user-data">
                          <div className="swift-data-box">
                            <p className="swift-data-label">Address</p>
                            <p className="swift-data-value">
                              {aadhaarAddress?.full || "-"}
                            </p>
                          </div>
                          <div className="swift-data-box">
                            <p className="swift-data-label">City</p>
                            <p className="swift-data-value">
                              {aadhaarAddress?.city || "-"}
                            </p>
                          </div>
                          <div className="swift-data-box">
                            <p className="swift-data-label">State</p>
                            <p className="swift-data-value">
                              {aadhaarAddress?.state || "-"}
                            </p>
                          </div>
                          <div className="swift-data-box">
                            <p className="swift-data-label">Pincode</p>
                            <p className="swift-data-value">
                              {aadhaarAddress?.pincode || "-"}
                            </p>
                          </div>
                        </div>
                        <p
                          className="user-incorrect-details"
                          onClick={resetAadhaar}
                        >
                          Incorrect address?
                        </p>
                      </div>
                      <div className="login-footer">
                        <div
                          className={`swift-button-icon ${BUTTON_SIZE}`}
                          onClick={handleSubmit}
                        >
                          <p>Confirm &amp; create account</p>
                        </div>
                      </div>
                    </>
                  ) : dlAvailable === null ? (
                    <div className="login-body">
                      <div className="popup-loader">
                        <Pulse />
                      </div>
                    </div>
                  ) : dlAvailable === true && !aadhaarMethod ? (
                    <>
                      <div className="login-body">
                        <div className="login-notes">
                          <p className="login-note">
                            {number} has a DigiLocker account. Share your
                            Aadhaar from DigiLocker and we will read the address
                            straight from it &mdash; no upload, no photo of a
                            card.
                          </p>
                        </div>
                        <p
                          className={
                            Error2 === "" ? "error-text hide" : "error-text"
                          }
                        >
                          {Error2}
                        </p>
                        <p
                          className="swift-pan-text"
                          onClick={() => {
                            setError2("");
                            setAadhaarMethod("upload");
                          }}
                        >
                          Upload a document instead
                        </p>
                      </div>
                      <div className="login-footer">
                        <div
                          className={`swift-button-icon ${BUTTON_SIZE}`}
                          onClick={startDigiLocker}
                        >
                          <p>Continue with DigiLocker</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="login-body">
                        <div
                          className="swift-button-icon"
                          onClick={() =>
                            document.getElementById("aadhaarInput").click()
                          }
                        >
                          <div className="button-icon">
                            <FileUpload
                              size={18}
                              color={COLOR_VARS["SWIFT_COLOR8"]}
                            />
                          </div>
                          <p>Upload Aadhaar</p>
                          <input
                            type="file"
                            id="aadhaarInput"
                            accept={AADHAAR_ACCEPT}
                            style={{ display: "none" }}
                            onChange={(e) =>
                              pickAadhaarFile(e.target.files?.[0])
                            }
                          />
                        </div>

                        {aadhaarFile && (
                          <p className="resend-message">{aadhaarFile.name}</p>
                        )}

                        <p
                          className={
                            Error2 === "" ? "error-text hide" : "error-text"
                          }
                        >
                          {Error2}
                        </p>

                        {needsPdfPassword && (
                          <div className="swift-input-box">
                            <p>PDF Password</p>
                            <input
                              className="swift-input"
                              type="password"
                              // Not an account password: it belongs to the
                              // file, so the browser must neither offer to
                              // save it nor autofill a stored one over it.
                              name="aadhaar-pdf-password"
                              autoComplete="off"
                              data-form-type="other"
                              data-lpignore="true"
                              data-1p-ignore="true"
                              value={pdfPassword}
                              onChange={(e) => setPdfPassword(e.target.value)}
                              placeholder="Password for the Aadhaar PDF"
                            />
                            <p
                              className={
                                Error1 === "" ? "error-text hide" : "error-text"
                              }
                            >
                              {Error1}
                            </p>
                          </div>
                        )}

                        <div className="login-notes">
                          <p className="login-note">
                            Upload the side of your Aadhaar that shows your
                            address. JPG, PNG or PDF, up to 8 MB.
                          </p>
                        </div>

                        {dlAvailable === true && (
                          <p
                            className="swift-pan-text"
                            onClick={() => {
                              setError1("");
                              setError2("");
                              setAadhaarMethod(null);
                            }}
                          >
                            Use DigiLocker instead
                          </p>
                        )}
                      </div>
                      <div className="login-footer">
                        <div
                          className={`swift-button-icon ${BUTTON_SIZE}`}
                          onClick={submitAadhaar}
                        >
                          <p>Next</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AccessSignupNew;
